import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/shared/auth';

function getInitialValue(storageKey: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = window.localStorage.getItem(storageKey);
    return stored === null ? fallback : stored === 'true';
  } catch {
    return fallback;
  }
}

/**
 * A boolean copy preference. Persists to the active user's `user_metadata` in
 * Supabase and falls back to `localStorage`.
 */
function useCopyPreference(storageKey: string, metadataKey: string, fallback: boolean) {
  const user = useUser();
  const [value, setValueState] = useState<boolean>(() => getInitialValue(storageKey, fallback));

  // Synchronize when the authenticated user profile loads or changes
  useEffect(() => {
    const stored: unknown = user?.user_metadata?.[metadataKey];
    if (typeof stored === 'boolean') {
      setValueState(stored);
      try {
        window.localStorage.setItem(storageKey, String(stored));
      } catch {
        /* storage restricted */
      }
    }
  }, [user, storageKey, metadataKey]);

  const setValue = useCallback(
    (next: boolean) => {
      setValueState(next);
      try {
        window.localStorage.setItem(storageKey, String(next));
      } catch {
        /* storage restricted */
      }
      if (user) {
        void supabase.auth.updateUser({
          data: { [metadataKey]: next },
        });
      }
    },
    [user, storageKey, metadataKey],
  );

  return [value, setValue] as const;
}

/** Whether grid copying includes column headers. Defaults to `false`. */
export function useCopyHeadersPreference() {
  return useCopyPreference('numtable_grid_copy_headers', 'copy_headers', false);
}

/** Whether number cells copy as displayed (USD 784) instead of the bare value. Defaults to `false`. */
export function useCopyNumberFormatPreference() {
  return useCopyPreference('numtable_grid_copy_number_format', 'copy_number_format', false);
}

/** Whether formula cells copy their formula instead of the result. Defaults to `true`. */
export function useCopyFormulasPreference() {
  return useCopyPreference('numtable_grid_copy_formulas', 'copy_formulas', true);
}
